import { FC, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Stack } from '@mui/material';
import {
  Panel,
  Footer,
  Button,
  DataLossPrompt,
  TextField,
  SwitchInput,
} from 'melp-design/components';
import { useMe } from 'state/Administrators';
import { Controller, SubmitHandler, useForm } from 'react-hook-form-latest';
import { useUpdateParentCompanySettings } from 'store/companies';
import { usePredefinedToasts } from 'utils/Toast';

interface Values {
  companyExpenseEnabled: boolean;
  companyExpenseEmail: string;
}

const INITIAL_VALUES: Values = {
  companyExpenseEnabled: false,
  companyExpenseEmail: '',
};

export const ClaimsSettings: FC = () => {
  const { t } = useTranslation();
  const { me } = useMe();
  const predefinedToasts = usePredefinedToasts();

  const { mutate: updateCompanySettings, isLoading: isUpdating } =
    useUpdateParentCompanySettings();

  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { isDirty },
  } = useForm<Values>({
    defaultValues: INITIAL_VALUES,
  });

  useEffect(() => {
    if (me) {
      reset({
        companyExpenseEnabled: me.parentCompany.settings.companyExpenseEnabled,
        companyExpenseEmail: me.parentCompany.settings.companyExpenseEmail,
      });
    }
  }, [me, reset]);

  const onSubmit: SubmitHandler<Values> = (values) => {
    updateCompanySettings(
      {
        companyExpenseEnabled: values.companyExpenseEnabled,
        companyExpenseEmail: values.companyExpenseEmail,
      },
      {
        onSuccess: () => {
          predefinedToasts.success.updated();
        },
      },
    );
  };

  const [companyExpenseEnabled] = watch(['companyExpenseEnabled']);

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack gap={2}>
          <Panel title={t('sidebar.companySettingsGeneralSettings')}>
            <Stack spacing={4}>
              <Controller
                name="companyExpenseEnabled"
                control={control}
                render={({ field: { name, value, onChange, ref } }) => {
                  return (
                    <SwitchInput
                      label={t('claims.companyExpenseEnabled.label')}
                      name={name}
                      value={value}
                      onChange={onChange}
                      ref={ref}
                    />
                  );
                }}
              />

              <Controller
                name="companyExpenseEmail"
                control={control}
                render={({ field: { name, value, onChange, ref } }) => {
                  return (
                    <TextField
                      label={t('claims.companyExpenseEmail.label')}
                      name={name}
                      value={value}
                      onChange={onChange}
                      type="email"
                      required={companyExpenseEnabled}
                      ref={ref}
                    />
                  );
                }}
              />
            </Stack>
          </Panel>
        </Stack>

        <Footer visible={isDirty}>
          <Stack direction="row" justifyContent="end" gap={2}>
            <Button
              label={t('common.cancel')}
              variant="neutral-outline"
              onClick={() => {
                reset(undefined, { keepDirty: false });
              }}
            />
            <Button
              label={t('common.save')}
              type="submit"
              disabled={isUpdating}
            />
          </Stack>
        </Footer>
      </form>

      <DataLossPrompt when={isDirty} />
    </>
  );
};
